import { useState, type ReactNode } from 'react';
import { api } from '../lib/api';
import { hasRole, isStaff, isTrainer, useAuth } from '../lib/auth';
import { useGym } from '../lib/gym';
import { useI18n } from '../lib/i18n';
import { Link, navigate, useLocation } from '../lib/router';
import { cx } from './components';
import { Icon } from './icons';

export interface NavItem { to: string; label: string; icon: string; exact?: boolean; hidden?: boolean; section?: string }

export function LanguageSelect({ compact }: { compact?: boolean }) {
  const i18n = useI18n();
  const { user } = useAuth();
  const available = i18n.locales.filter((l) => l.available !== false);
  const list = available.length ? available : [{ code: 'en', nativeName: 'English' }, { code: 'nl', nativeName: 'Nederlands' }];
  return (
    <label className={cx('lang', compact && 'compact')}>
      <Icon name="globe" size={16} />
      <select aria-label={i18n.t('common.language')} value={i18n.locale} onChange={(e) => {
        i18n.setLocale(e.target.value);
        if (user) api('/v1/me', { method: 'PATCH', body: { locale: e.target.value } }).catch(() => undefined);
      }}>
        {!list.some((l) => l.code === i18n.locale) && <option value={i18n.locale}>{i18n.locale}</option>}
        {list.map((l) => <option key={l.code} value={l.code}>{l.nativeName}</option>)}
      </select>
    </label>
  );
}

export function Brand({ to }: { to?: string }) {
  const gym = useGym();
  const logo = gym.gym.branding?.logoUrl;
  return (
    <Link to={to ?? gym.base} className="brand">
      {logo ? <img src={logo} alt="" /> : <span className="brand-mark">{gym.gym.name.slice(0, 1)}</span>}
      <span>{gym.gym.name}</span>
    </Link>
  );
}

function AreaSwitch() {
  const gym = useGym();
  const i18n = useI18n();
  const { path } = useLocation();
  const m = gym.membership;
  if (!m) return null;
  const areas = [
    { id: 'admin', to: `${gym.base}/admin`, show: isStaff(m) },
    { id: 'trainer', to: `${gym.base}/trainer`, show: isTrainer(m) },
    { id: 'member', to: `${gym.base}/planner`, show: Boolean(m.personId) && (isStaff(m) || isTrainer(m)) },
  ].filter((a) => a.show);
  if (areas.length < 2) return null;
  const current = path.includes('/admin') ? 'admin' : path.includes('/trainer') ? 'trainer' : 'member';
  return (
    <div className="segmented" role="group">
      {areas.map((a) => <Link key={a.id} to={a.to} className={cx(current === a.id && 'active')}>{i18n.t(`area.${a.id}`)}</Link>)}
    </div>
  );
}

/** Shown on installations where payments are simulated, so nobody mistakes a test server for the real thing. */
export function TestModeBanner() {
  const gym = useGym();
  const i18n = useI18n();
  return gym.gym.testMode ? <div className="test-banner" role="status">{i18n.t('common.test_mode')}</div> : null;
}

export function Shell({ nav, children, bottomNav }: { nav: NavItem[]; children: ReactNode; bottomNav?: boolean }) {
  const { path } = useLocation();
  const { user, tenants, logout } = useAuth();
  const i18n = useI18n();
  const gym = useGym();
  const [open, setOpen] = useState(false);
  const visible = (() => {
    const items = nav.filter((n) => !n.hidden);
    const order: (string | undefined)[] = [];
    for (const n of items) if (!order.includes(n.section)) order.push(n.section);
    return order.flatMap((sec) => items.filter((n) => n.section === sec));
  })();
  const active = (n: NavItem): boolean => (n.exact ? path === n.to : path === n.to || path.startsWith(`${n.to}/`));
  let lastSection: string | undefined;
  return (
    <div className={cx('shell', bottomNav && 'has-bottom-nav')}>
      <a className="skip-link" href="#main">{i18n.t('common.skip_to_content')}</a>
      <TestModeBanner />
      <header className="topbar">
        <button type="button" className="icon-btn only-mobile" aria-label={i18n.t('common.menu')} onClick={() => setOpen(!open)}><Icon name="menu" /></button>
        <Brand />
        <div className="topbar-mid"><AreaSwitch /></div>
        <div className="row gap-sm">
          <LanguageSelect compact />
          {tenants.length > 1 && (
            <select className="input input-sm hide-mobile" aria-label={i18n.t('common.switch_gym')} value={gym.slug} onChange={(e) => navigate(`/g/${e.target.value}`)}>
              {tenants.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
            </select>
          )}
          {user && <Link to="/account" className="icon-btn" aria-label={i18n.t('nav.account')} title={user.email}><Icon name="user" /></Link>}
          {user && <button type="button" className="icon-btn" aria-label={i18n.t('common.logout')} title={i18n.t('common.logout')} onClick={logout}><Icon name="logout" /></button>}
        </div>
      </header>
      <div className="shell-body">
        <nav className={cx('sidebar', open && 'open')} onClick={() => setOpen(false)}>
          {visible.map((n) => {
            const heading = n.section && n.section !== lastSection ? <div className="nav-section" key={`s-${n.section}`}>{i18n.t(`nav.section.${n.section}`)}</div> : null;
            lastSection = n.section;
            return (
              <div key={n.to}>
                {heading}
                <Link to={n.to} className={cx('nav-link', active(n) && 'active')}><Icon name={n.icon} /><span>{n.label}</span></Link>
              </div>
            );
          })}
        </nav>
        <main className="content" id="main">{children}</main>
      </div>
      {bottomNav && (
        <nav className="bottom-nav">
          {visible.slice(0, visible.length > 5 ? 4 : 5).map((n) => (
            <Link key={n.to} to={n.to} className={cx(active(n) && 'active')}><Icon name={n.icon} size={22} /><span>{n.label}</span></Link>
          ))}
          {visible.length > 5 && (
            <button type="button" className={cx(open && 'active')} onClick={() => setOpen(!open)}>
              <Icon name="menu" size={22} /><span>{i18n.t('mobile.more')}</span>
            </button>
          )}
        </nav>
      )}
    </div>
  );
}

export function memberNav(base: string, t: (k: string) => string): NavItem[] {
  return [
    { to: `${base}/planner`, label: t('nav.planner'), icon: 'calendar' },
    { to: `${base}/bookings`, label: t('nav.bookings'), icon: 'list' },
    { to: `${base}/training`, label: t('nav.training'), icon: 'whistle' },
    { to: `${base}/book`, label: t('nav.book_trainer'), icon: 'user' },
    { to: `${base}/shop`, label: t('nav.shop'), icon: 'bag' },
    { to: `${base}/events`, label: t('nav.events'), icon: 'ticket' },
    { to: `${base}/coaching`, label: t('nav.coaching'), icon: 'chat' },
    { to: `${base}/body`, label: t('nav.body'), icon: 'chart' },
    { to: `${base}/pass`, label: t('nav.pass'), icon: 'ticket' },
    { to: `${base}/profile`, label: t('nav.profile'), icon: 'user' },
    { to: `${base}/chat`, label: t('nav.chat'), icon: 'chat' },
  ];
}

export function trainerNav(base: string, t: (k: string) => string): NavItem[] {
  return [
    { to: `${base}/trainer`, label: t('nav.my_lessons'), icon: 'calendar', exact: true },
    { to: `${base}/trainer/open-lessons`, label: t('nav.open_lessons'), icon: 'whistle' },
    { to: `${base}/trainer/availability`, label: t('nav.availability'), icon: 'clock' },
    { to: `${base}/trainer/earnings`, label: t('nav.earnings'), icon: 'euro' },
    { to: `${base}/trainer/profile`, label: t('nav.trainer_profile'), icon: 'user' },
  ];
}

export function adminNav(base: string, t: (k: string) => string, roles: string[]): NavItem[] {
  const m = { roles } as { roles: string[] };
  const mgmt = hasRole(m as never, 'owner', 'manager');
  const money = hasRole(m as never, 'owner', 'manager', 'finance');
  const ops = hasRole(m as never, 'owner', 'manager', 'desk');
  const owner = hasRole(m as never, 'owner');
  const a = `${base}/admin`;
  return [
    { to: a, label: t('nav.dashboard'), icon: 'home', exact: true, section: 'daily' },
    { to: `${a}/planner`, label: t('nav.planner'), icon: 'calendar', section: 'daily' },
    { to: `${a}/open-lessons`, label: t('nav.open_lessons'), icon: 'whistle', hidden: !mgmt, section: 'daily' },
    { to: `${a}/clients`, label: t('nav.clients'), icon: 'users', section: 'daily' },
    { to: `${a}/pipeline`, label: t('nav.pipeline'), icon: 'funnel', hidden: !ops, section: 'daily' },
    { to: `${a}/retention`, label: t('nav.retention'), icon: 'heart', hidden: !ops, section: 'daily' },
    { to: `${a}/inbox`, label: t('nav.inbox'), icon: 'inbox', hidden: !ops, section: 'daily' },
    { to: `${a}/tasks`, label: t('nav.tasks'), icon: 'check', hidden: !ops, section: 'daily' },
    { to: `${a}/kiosk`, label: t('nav.kiosk'), icon: 'check', hidden: !ops, section: 'daily' },
    { to: `${a}/trainers`, label: t('nav.trainers'), icon: 'dumbbell', section: 'team' },
    { to: `${a}/staff`, label: t('nav.staff'), icon: 'shield', hidden: !mgmt, section: 'team' },
    { to: `${a}/r/shifts`, label: t('nav.shifts'), icon: 'clock', hidden: !mgmt, section: 'team' },
    { to: `${a}/swaps`, label: t('nav.swaps'), icon: 'refresh', hidden: !ops, section: 'team' },
    { to: `${a}/qualifications`, label: t('nav.qualifications'), icon: 'shield', hidden: !mgmt, section: 'team' },
    { to: `${a}/incidents`, label: t('nav.incidents'), icon: 'shield', hidden: !ops, section: 'daily' },
    { to: `${a}/checklists`, label: t('nav.checklists'), icon: 'check', hidden: !ops, section: 'daily' },
    { to: `${a}/website`, label: t('nav.website'), icon: 'globe', hidden: !mgmt, section: 'business' },
    { to: `${a}/equipment`, label: t('nav.equipment'), icon: 'clock', hidden: !mgmt, section: 'team' },
    { to: `${a}/import`, label: t('nav.import'), icon: 'upload', hidden: !mgmt, section: 'business' },
    { to: `${a}/dunning`, label: t('nav.dunning'), icon: 'euro', hidden: !money, section: 'business' },
    { to: `${a}/contracts`, label: t('nav.contracts'), icon: 'doc', hidden: !mgmt, section: 'team' },
    { to: `${a}/lessons`, label: t('nav.lessons'), icon: 'list', section: 'offer' },
    { to: `${a}/memberships`, label: t('nav.memberships'), icon: 'card', section: 'offer' },
    { to: `${a}/tokens`, label: t('nav.tokens'), icon: 'token', section: 'offer' },
    { to: `${a}/shop`, label: t('nav.shop'), icon: 'bag', section: 'offer' },
    { to: `${a}/events`, label: t('nav.events'), icon: 'ticket', section: 'offer' },
    { to: `${a}/locations`, label: t('nav.locations'), icon: 'pin', section: 'offer' },
    { to: `${a}/finance`, label: t('nav.finance'), icon: 'euro', hidden: !money, section: 'business' },
    { to: `${a}/reports`, label: t('nav.reports'), icon: 'chart', section: 'business' },
    { to: `${a}/assistant`, label: t('nav.assistant'), icon: 'chat', hidden: !owner, section: 'business' },
    { to: `${a}/automations`, label: t('nav.automations'), icon: 'zap', hidden: !mgmt, section: 'business' },
    { to: `${a}/settings`, label: t('nav.settings'), icon: 'settings', hidden: !mgmt, section: 'business' },
    { to: `${a}/integrations`, label: t('nav.integrations'), icon: 'plug', hidden: !mgmt, section: 'business' },
    { to: `${a}/billing`, label: t('nav.subscription'), icon: 'card', hidden: !owner, section: 'business' },
  ];
}

/** Simple centered layout for login and public flows. */
export function Centered({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="centered">
      <div className={cx('centered-card', wide && 'wide')}>{children}</div>
      <div className="centered-foot"><LanguageSelect /></div>
    </div>
  );
}
